/** AI B2B Leads: numbered paging for the leads table. Shows nothing until there is more than one page. */
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { pageWindow } from "./types";

interface Props {
  page: number;
  pageSize: number;
  totalItems: number;
  onPage: (page: number) => void;
}

export function LeadsPager({ page, pageSize, totalItems, onPage }: Props) {
  const { t } = useTranslation();
  if (totalItems <= pageSize) return null;
  const pages = Math.ceil(totalItems / pageSize);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, totalItems);

  return (
    <nav className="flex flex-col items-center justify-between gap-2 sm:flex-row" aria-label={t("prospectLeads.leads.pagination")} data-testid="pager">
      <span className="text-sm text-muted-foreground" data-testid="pager-range">{t("prospectLeads.leads.showing", { from, to, total: totalItems })}</span>
      <div className="flex flex-wrap items-center justify-center gap-1">
        <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">{t("prospectLeads.leads.prev")}</Button>
        {pageWindow(page, pages).map((p, i) =>
          p === "gap" ? (
            <span key={`gap-${i}`} className="px-1 text-sm text-muted-foreground" aria-hidden>…</span>
          ) : (
            <Button
              key={p}
              size="sm"
              variant={p === page ? "default" : "outline"}
              aria-label={t("prospectLeads.leads.goToPage", { page: p })}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onPage(p)}
              data-testid={`pager-page-${p}`}
            >
              {p}
            </Button>
          ),
        )}
        <Button size="sm" variant="outline" disabled={page >= pages} onClick={() => onPage(page + 1)} data-testid="pager-next">{t("prospectLeads.leads.next")}</Button>
      </div>
    </nav>
  );
}
